import React, { useRef, useState } from "react";
import { previsualizarFacturaRepuestos, enviarFacturaRepuestos } from "../api";
import { fmtMoney, bogotaToday } from "../utils";
import { RepIcon } from "./RepuestosUI";
import {
  MAX_XML,
  MAX_PDF,
  fmtSize,
  money,
  uploadErr,
  FacturaRows,
  Checks,
  countChecks,
} from "./RepuestosComun";

const PDF_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const PDF_EXT = /\.(pdf|jpe?g|png)$/i;
const PDF_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

/** Solo dígitos (pesos enteros), sin ceros a la izquierda. */
const soloDigitos = (s) =>
  String(s || "").replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 13);
/** "2400000" → "2.400.000". */
const conPuntos = (d) => (d ? String(d).replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "");
const sumar = (x, y) => {
  const s = Number(x || 0) + Number(y || 0);
  return s > 0 ? String(s) : "";
};

function FilePick({ accept, file, onPick, text }) {
  return (
    <label className="rep-file">
      <input
        type="file"
        accept={accept}
        aria-label={text}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = ""; // permite volver a escoger el mismo archivo
          if (f) onPick(f);
        }}
      />
      <span className="rep-file-icon"><RepIcon name={file ? "file" : "upload"} size={24}/></span>
      <span className="rep-file-info"><span className="rep-file-btn">{file ? "Cambiar archivo" : text}</span><span className="rep-file-name">{file ? `${file.name} · ${fmtSize(file.size)}` : "Seleccione un archivo desde su dispositivo"}</span></span>
      {file && <RepIcon name="check" size={20}/>}
    </label>
  );
}

/**
 * Facturar una autorización (RECIBIDA o DEVUELTA).
 * Electrónica: XML (obligatorio) + PDF (opcional); al escoger el XML se
 * previsualiza (no guarda nada) y con checks en rojo no se deja enviar.
 * No electrónica: PDF o foto + número, fecha, subtotal, IVA y total.
 */
export default function RepuestosFacturar({ a, onSent }) {
  const [tipo, setTipo] = useState(""); // "" | "e" (electrónica) | "p" (no electrónica)
  const [xml, setXml] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [prev, setPrev] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [prevError, setPrevError] = useState("");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [iva, setIva] = useState("");
  const [total, setTotal] = useState("");
  const [totalManual, setTotalManual] = useState(false);
  const [fileError, setFileError] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const seq = useRef(0);
  const hoy = bogotaToday();

  async function leerXml(file) {
    const n = ++seq.current;
    setPrev(null);
    setPrevError("");
    setLeyendo(true);
    try {
      const r = await previsualizarFacturaRepuestos(a.id, file);
      if (n === seq.current) setPrev(r || {});
    } catch (e) {
      if (n === seq.current) setPrevError(uploadErr(e, "No se pudo leer el XML."));
    } finally {
      if (n === seq.current) setLeyendo(false);
    }
  }

  function pickXml(f) {
    setFileError("");
    setError("");
    if (!/\.xml$/i.test(f.name) && !/xml/i.test(f.type || "")) {
      setFileError("Seleccione el archivo XML de la factura electrónica (extensión .xml).");
      return;
    }
    if (f.size > MAX_XML) {
      setFileError(`El XML pesa ${fmtSize(f.size)}; el máximo es 3 MB.`);
      return;
    }
    setXml(f);
    leerXml(f);
  }

  function pickPdf(f) {
    setFileError("");
    setError("");
    if (!PDF_TYPES.includes(f.type) && !PDF_EXT.test(f.name)) {
      setFileError("Solo se aceptan archivos PDF, JPG o PNG.");
      return;
    }
    if (f.size > MAX_PDF) {
      setFileError(`El archivo pesa ${fmtSize(f.size)}; el máximo es 10 MB.`);
      return;
    }
    setPdf(f);
  }

  function cambiarSubtotal(v) {
    const d = soloDigitos(v);
    setSubtotal(d);
    if (!totalManual) setTotal(sumar(d, iva));
  }

  function cambiarIva(v) {
    const d = soloDigitos(v);
    setIva(d);
    if (!totalManual) setTotal(sumar(subtotal, d));
  }

  function escogerTipo(t) {
    setTipo(t);
    setError("");
    setFileError("");
  }

  const checks = prev?.validation?.checks || [];
  const nErr = countChecks(checks, "error");
  const nWarn = countChecks(checks, "warning");
  const hasErr = !!prev && (prev.validation?.ok === false || nErr > 0);
  const listoE = tipo === "e" && !!xml && !!prev && !hasErr && !leyendo;
  const descuadre =
    tipo === "p" && Number(total) > 0 && Number(subtotal || 0) + Number(iva || 0) !== Number(total);
  const difAutorizado = tipo === "p" && Number(total) > 0 && Number(total) !== Number(a.amount);

  async function enviar() {
    setError("");
    let payload;
    let resumen;
    if (tipo === "e") {
      if (!xml) {
        setError("Seleccione el XML de la factura electrónica.");
        return;
      }
      if (!prev || leyendo) {
        setError("Espere a que termine la lectura del XML.");
        return;
      }
      if (hasErr) {
        setError("La factura tiene errores (en rojo) y no se puede enviar.");
        return;
      }
      payload = { isElectronic: true, xml, pdf };
      resumen = `la factura ${prev?.invoice?.number || ""} por ${money(prev?.invoice?.total)}`;
    } else {
      if (!pdf) {
        setError("Adjunte el PDF o la foto de la factura.");
        return;
      }
      if (!numero.trim()) {
        setError("Ingrese el número de la factura.");
        return;
      }
      if (!fecha) {
        setError("Ingrese la fecha de la factura.");
        return;
      }
      if (fecha > hoy) {
        setError("La fecha de la factura no puede ser posterior a hoy.");
        return;
      }
      if (!(Number(subtotal) > 0)) {
        setError("Ingrese el subtotal antes de IVA.");
        return;
      }
      if (!(Number(total) > 0)) {
        setError("Ingrese el total de la factura.");
        return;
      }
      payload = {
        isElectronic: false,
        pdf,
        invoiceNumber: numero.trim(),
        invoiceDate: fecha,
        subtotal: Number(subtotal),
        tax: Number(iva || 0),
        total: Number(total),
      };
      resumen = `la factura ${numero.trim()} por ${fmtMoney(total)}`;
    }
    if (!window.confirm(`¿Enviar ${resumen} a revisión de contabilidad?`)) return;

    setEnviando(true);
    try {
      const r = await enviarFacturaRepuestos(a.id, payload);
      setEnviando(false);
      await onSent(r);
    } catch (e) {
      setError(uploadErr(e, "No se pudo enviar la factura."));
      setEnviando(false);
    }
  }

  return (
    <div className="rep-invoice-form">
      <fieldset className="rep-invoice-type"><legend>Tipo de factura</legend>
      <div className="rep-choice">
        <button type="button" aria-pressed={tipo === "e"} className={tipo === "e" ? "on" : ""} onClick={() => escogerTipo("e")}>
          <span className="rep-choice-top"><RepIcon name="document" size={20}/><span className="rep-choice-radio"/></span>
          Factura electrónica
          <small>XML obligatorio y soporte opcional.</small>
        </button>
        <button type="button" aria-pressed={tipo === "p"} className={tipo === "p" ? "on" : ""} onClick={() => escogerTipo("p")}>
          <span className="rep-choice-top"><RepIcon name="file" size={20}/><span className="rep-choice-radio"/></span>
          No electrónica
          <small>PDF o foto y datos de la factura.</small>
        </button>
      </div></fieldset>

      {tipo === "e" && (
        <>
          <div className="rep-field-label">Archivo XML <span>Obligatorio</span></div>
          <FilePick accept=".xml,text/xml,application/xml" file={xml} onPick={pickXml} text="Escoger XML" />
          <div className="rep-hint">
            Máximo 3 MB. Si recibió un ZIP, descomprímalo y seleccione el archivo XML.
          </div>

          <div className="rep-field-label">PDF o imagen de la factura <span>Opcional</span></div>
          <FilePick accept={PDF_ACCEPT} file={pdf} onPick={pickPdf} text="Escoger PDF" />
          <div className="rep-hint">Máximo 10 MB.</div>
          {pdf && (
            <button type="button" className="btn-sm ghost" style={{ marginTop: 8 }} onClick={() => setPdf(null)}>
              Quitar PDF
            </button>
          )}

          {fileError && <div className="banner err" style={{ marginTop: 14 }}>{fileError}</div>}

          {leyendo && (
            <div className="center" style={{ padding: 20 }}>
              <div className="spinner" />
              <div className="rep-hint">Leyendo el XML…</div>
            </div>
          )}

          {prevError && !leyendo && (
            <div className="banner err" style={{ marginTop: 14 }}>
              {prevError}
              {xml && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn-sm" onClick={() => leerXml(xml)}>
                    Volver a leer el XML
                  </button>
                </div>
              )}
            </div>
          )}

          {prev && !leyendo && (
            <>
              <div className="section">Datos de la factura electrónica</div>
              <FacturaRows inv={prev.invoice || {}} />

              <div className="section">Validación</div>
              {hasErr ? (
                <div className="banner err">
                  <b>No se puede enviar esta factura.</b>{" "}
                  {nErr > 0
                    ? `Tiene ${nErr} ${nErr === 1 ? "problema" : "problemas"} en rojo. `
                    : "La validación encontró problemas. "}
                  Corrija la factura (o genere una nueva en su sistema de facturación electrónica) y
                  vuelva a cargar el XML. Si considera que hay un error, comuníquese con la empresa de
                  transporte.
                </div>
              ) : nWarn > 0 ? (
                <div className="banner warn">
                  Hay {nWarn} {nWarn === 1 ? "advertencia" : "advertencias"} para revisión. Puede enviarla;
                  contabilidad {nWarn === 1 ? "la revisará" : "las revisará"}.
                </div>
              ) : (
                <div className="banner rep-ok">Validación completada. Puede enviar la factura.</div>
              )}
              <Checks checks={checks} />
            </>
          )}
        </>
      )}

      {tipo === "p" && (
        <>
          <div className="rep-field-label">PDF o foto de la factura <span>Obligatorio</span></div>
          <FilePick accept={PDF_ACCEPT} file={pdf} onPick={pickPdf} text="Escoger archivo" />
          <div className="rep-hint">
            PDF, JPG o PNG, máximo 10 MB. El número, la fecha y los valores deben ser legibles.
          </div>
          {fileError && <div className="banner err" style={{ marginTop: 14 }}>{fileError}</div>}

          <div className="row2">
            <div>
              <label htmlFor={`rep-numero-${a.id}`}>Número de la factura</label>
              <input
                id={`rep-numero-${a.id}`}
                type="text"
                value={numero}
                maxLength={40}
                onChange={(e) => setNumero(e.target.value)}
                placeholder="Ej: 1234"
              />
            </div>
            <div>
              <label htmlFor={`rep-fecha-${a.id}`}>Fecha de la factura</label>
              <input id={`rep-fecha-${a.id}`} type="date" value={fecha} max={hoy} onChange={(e) => setFecha(e.target.value)} />
            </div>
          </div>
          <div className="row2">
            <div>
              <label htmlFor={`rep-subtotal-${a.id}`}>Subtotal (antes de IVA)</label>
              <input
                id={`rep-subtotal-${a.id}`}
                type="text"
                inputMode="numeric"
                value={conPuntos(subtotal)}
                onChange={(e) => cambiarSubtotal(e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label htmlFor={`rep-iva-${a.id}`}>IVA</label>
              <input
                id={`rep-iva-${a.id}`}
                type="text"
                inputMode="numeric"
                value={conPuntos(iva)}
                onChange={(e) => cambiarIva(e.target.value)}
                placeholder="0 si no aplica"
              />
            </div>
          </div>
          <label htmlFor={`rep-total-${a.id}`}>Total de la factura</label>
          <input
            id={`rep-total-${a.id}`}
            type="text"
            inputMode="numeric"
            value={conPuntos(total)}
            onChange={(e) => {
              setTotalManual(true);
              setTotal(soloDigitos(e.target.value));
            }}
            placeholder="0"
          />
          <div className="rep-hint">
            Se calcula a partir del subtotal y el IVA. Puede ajustarlo al total del documento. Valores en pesos,
            sin centavos.
          </div>
          {descuadre && (
            <div className="banner warn" style={{ marginTop: 12 }}>
              Verifique: subtotal + IVA da {fmtMoney(Number(subtotal || 0) + Number(iva || 0))} y el total
              dice {fmtMoney(total)}.
            </div>
          )}
          {difAutorizado && (
            <div className="banner info" style={{ marginTop: 12 }}>
              El total ({fmtMoney(total)}) es diferente al valor autorizado ({fmtMoney(a.amount)}).
            </div>
          )}
        </>
      )}

      {tipo && (
        <>
          {error && <div className="banner err" style={{ marginTop: 14 }}>{error}</div>}
          <button
            type="button"
            className="btn accent"
            style={{ marginTop: 18 }}
            disabled={enviando || (tipo === "e" && !listoE)}
            onClick={enviar}
          >
            <RepIcon name="upload" size={18}/>{enviando ? "Enviando…" : "Enviar factura a revisión"}
          </button>
          {tipo === "e" && !xml && (
            <div className="rep-hint" style={{ textAlign: "center" }}>Seleccione el XML para continuar.</div>
          )}
        </>
      )}
    </div>
  );
}
